import {
  Description as HeadlessDescription,
  Field as HeadlessField,
  Input as HeadlessInput,
  Label as HeadlessLabel,
  Legend as HeadlessLegend,
} from '@headlessui/react';
import { ComponentProps, forwardRef, InputHTMLAttributes } from 'react';
import { cn } from '../../lib/classNames';

/**
 *
 * This file contains "raw" form elements - essentially wrappers around HeadlessUI.
 * For out-of-the-box form elements, see CommonFields.tsx
 */

export const Field = ({
  children,
  ...rest
}: ComponentProps<typeof HeadlessField>) => (
  <HeadlessField {...rest}>{children}</HeadlessField>
);

export const Label = ({
  children,
  className,
  ...rest
}: ComponentProps<typeof HeadlessLabel>) => (
  <HeadlessLabel
    className={cn('text-base font-medium text-subheading', className)}
    {...rest}
  >
    {children}
  </HeadlessLabel>
);

export const Legend = ({
  children,
  className,
  ...rest
}: ComponentProps<typeof HeadlessLegend>) => (
  <HeadlessLegend
    className={cn('text-base font-medium text-subheading', className)}
    {...rest}
  >
    {children}
  </HeadlessLegend>
);

export const Description = ({
  children,
  className,
  ...rest
}: ComponentProps<typeof HeadlessDescription>) => (
  <HeadlessDescription
    className={cn('text-sm text-grey-60', className)}
    {...rest}
  >
    {children}
  </HeadlessDescription>
);

export const ErrorMessage = ({
  children,
  className,
  ...rest
}: ComponentProps<typeof HeadlessDescription>) => (
  <HeadlessDescription
    role="alert"
    className={cn('text-sm text-red', className)}
    {...rest}
  >
    {children}
  </HeadlessDescription>
);

/**
 * HeadlessUI handles a11y-attributes (htmlFor etc) automatically
 *
 * @example
 * import { Input, Field, Label, Description, ErrorMessage } from '@otovo/shared/components/Form/FormElements';
 *
 * <Field>
 *   <Label>Email</Label>
 *   <Input type="email" />
 *   {!error && <Description>We'll never share your email.</Description>}
 *   {error && <ErrorMessage>Invalid email</ErrorMessage>}
 * </Field>
 *
 */

type InputProps = ComponentProps<typeof HeadlessInput> &
  InputHTMLAttributes<HTMLInputElement> &
  // Make type required
  NonNullable<{
    type: InputHTMLAttributes<HTMLInputElement>['type'];
  }>;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...rest }, ref) => (
    <HeadlessInput
      ref={ref}
      className={cn(
        'mt-2 mb-1 w-full rounded-sm border-0 ring-1 transition-all duration-200 hover:ring-2 focus:ring-2 focus:outline-none',
        'ring-grey-10 hover:ring-grey-10 focus:ring-digital',
        'data-[invalid]:ring-red data-[invalid]:hover:ring-red data-[invalid]:focus:ring-red',
        'data-disabled:bg-grey-5',
        className,
      )}
      {...rest}
    />
  ),
);

Input.displayName = 'Input';
